import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
interface OrganizationFormData {
  name: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  defaultTimezone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  defaultFulfillmentMode: 'ship-to-home' | 'bulk-ship' | 'hybrid';
}
interface CreateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: OrganizationFormData) => void;
}
const TIMEZONES = [{
  value: 'America/New_York',
  label: 'Eastern Time (America/New_York)'
}, {
  value: 'America/Chicago',
  label: 'Central Time (America/Chicago)'
}, {
  value: 'America/Denver',
  label: 'Mountain Time (America/Denver)'
}, {
  value: 'America/Los_Angeles',
  label: 'Pacific Time (America/Los_Angeles)'
}, {
  value: 'America/Detroit',
  label: 'Eastern Time - Detroit (America/Detroit)'
}, {
  value: 'America/Phoenix',
  label: 'Mountain Time - Phoenix (America/Phoenix)'
}];
export function CreateOrganizationModal({
  isOpen,
  onClose,
  onSave
}: CreateOrganizationModalProps) {
  const [formData, setFormData] = useState<OrganizationFormData>({
    name: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    defaultTimezone: 'America/Chicago',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    defaultFulfillmentMode: 'ship-to-home'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Organization name is required';
    if (!formData.contactName.trim()) newErrors.contactName = 'Primary contact name is required';
    if (!formData.contactEmail.trim()) newErrors.contactEmail = 'Primary contact email is required';
    if (!formData.defaultTimezone) newErrors.defaultTimezone = 'Default timezone is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = () => {
    if (!validate()) return;
    onSave(formData);
    onClose();
    // Reset form
    setFormData({
      name: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      defaultTimezone: 'America/Chicago',
      address1: '',
      address2: '',
      city: '',
      state: '',
      postalCode: '',
      defaultFulfillmentMode: 'ship-to-home'
    });
    setErrors({});
  };
  return <Modal isOpen={isOpen} onClose={onClose} title="Create Organization" size="lg" footer={<>
          <Button variant="outline" onClick={onClose} data-id="element-118">
            Cancel
          </Button>
          <Button onClick={handleSave} data-id="element-119">Create Organization</Button>
        </>} data-id="element-117">
      <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2" data-id="element-120">
        {/* Basic Information */}
        <div data-id="element-121">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-122">
            Basic Information
          </h4>
          <div className="space-y-4" data-id="element-123">
            <Input label="Organization Name *" value={formData.name} onChange={e => setFormData(prev => ({
            ...prev,
            name: e.target.value
          }))} error={errors.name} placeholder="e.g., Lincoln High School" data-id="element-124" />

            <div className="grid grid-cols-2 gap-4" data-id="element-125">
              <Input label="Primary Contact Name *" value={formData.contactName} onChange={e => setFormData(prev => ({
              ...prev,
              contactName: e.target.value
            }))} error={errors.contactName} placeholder="John Doe" data-id="element-126" />
              <Input label="Primary Contact Email *" type="email" value={formData.contactEmail} onChange={e => setFormData(prev => ({
              ...prev,
              contactEmail: e.target.value
            }))} error={errors.contactEmail} placeholder="john@school.edu" data-id="element-127" />
            </div>

            <Input label="Primary Contact Phone" type="tel" value={formData.contactPhone} onChange={e => setFormData(prev => ({
            ...prev,
            contactPhone: e.target.value
          }))} placeholder="(555) 123-4567" data-id="element-128" />
          </div>
        </div>

        {/* Defaults */}
        <div className="border-t border-gray-200 pt-6" data-id="element-129">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-130">
            Default Settings
          </h4>
          <div className="space-y-4" data-id="element-131">
            <Select label="Default Timezone *" value={formData.defaultTimezone} onChange={e => setFormData(prev => ({
            ...prev,
            defaultTimezone: e.target.value
          }))} options={TIMEZONES} error={errors.defaultTimezone} data-id="element-132" />

            <Select label="Default Fulfillment Mode *" value={formData.defaultFulfillmentMode} onChange={e => setFormData(prev => ({
            ...prev,
            defaultFulfillmentMode: e.target.value as any
          }))} options={[{
            value: 'ship-to-home',
            label: 'Ship-to-home'
          }, {
            value: 'bulk-ship',
            label: 'Bulk ship to school/organization'
          }, {
            value: 'hybrid',
            label: 'Hybrid (customer chooses)'
          }]} data-id="element-133" />
          </div>
        </div>

        {/* Default Delivery Address */}
        <div className="border-t border-gray-200 pt-6" data-id="element-134">
          <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-135">
            Default Delivery Address
          </h4>
          <p className="text-xs text-gray-500 mb-4" data-id="element-136">
            Optional but recommended for bulk shipping. Will be used as default
            when creating new stores.
          </p>
          <div className="space-y-4" data-id="element-137">
            <Input label="Address Line 1" value={formData.address1} onChange={e => setFormData(prev => ({
            ...prev,
            address1: e.target.value
          }))} placeholder="123 School Lane" data-id="element-138" />

            <Input label="Address Line 2" value={formData.address2} onChange={e => setFormData(prev => ({
            ...prev,
            address2: e.target.value
          }))} placeholder="Building A, Room 101" data-id="element-139" />

            <div className="grid grid-cols-3 gap-4" data-id="element-140">
              <Input label="City" value={formData.city} onChange={e => setFormData(prev => ({
              ...prev,
              city: e.target.value
            }))} placeholder="Springfield" data-id="element-141" />
              <Input label="State" value={formData.state} onChange={e => setFormData(prev => ({
              ...prev,
              state: e.target.value
            }))} placeholder="IL" data-id="element-142" />
              <Input label="Postal Code" value={formData.postalCode} onChange={e => setFormData(prev => ({
              ...prev,
              postalCode: e.target.value
            }))} placeholder="62704" data-id="element-143" />
            </div>
          </div>
        </div>
      </div>
    </Modal>;
}